import React, { useState } from 'react';
import { Flex, Text, Box, Tooltip } from '@chakra-ui/react';
import { ButtonText } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faQuestion,
  faTrash
} from '@fortawesome/pro-light-svg-icons';
import { faUserSlash } from '@fortawesome/pro-solid-svg-icons';
import {
  ACTIVITY_TYPE,
  ACTIVITY_TYPE_INFO,
  ACTIVITY_TYPE_VALUES,
  allPhaseMap,
  IRoleType,
  ISubmissionWorkflowState
} from '@publishing-engine/domain/models';
import { generateActivityCopyText } from './activityLog';
import EmailViewModal from './EmailViewModal';
import { ActivityLogResponse } from './ActivityTab';
import DisplayMultilineText from '../../DisplayMultilineText';
import { formatDateAndTime } from '../../../utils/date';
import { SubmissionPhase } from '../../../pages/Organization/Workspace/Submissions';

const DeactivatedUserIcon = () => (
  <Tooltip
    hasArrow
    placement="top"
    label="User Deactivated"
    borderRadius={4}
    shouldWrapChildren
  >
    <FontAwesomeIcon icon={faUserSlash} style={{ color: 'red' }} />
  </Tooltip>
);

type ActivityBaseProps = ActivityLogResponse & {
  roleContext: IRoleType;
  reviewId?: string;
  fieldLabelMap?: Record<string, string>;
};
// Base activity component
const ActivityBase: React.FC<ActivityBaseProps> = activity => {
  const {
    content,
    userFirstName,
    userLastName,
    userStatus,
    reviewerFirstName,
    reviewerLastName,
    reviewerStatus,
    reviewerEmail,
    date,
    phase,
    type,
    action,
    roleContext,
    reviewId,
    proxyUserId,
    proxyUserFirstName,
    proxyUserLastName,
    proxyUserStatus,
    submissionId,
    submissionContextId,
    isSystem,
    fieldLabelMap
  } = activity;
  let icon =
    ACTIVITY_TYPE_INFO[activity.type as ACTIVITY_TYPE_VALUES]?.icon ||
    faQuestion;

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  if (type === ACTIVITY_TYPE.SubmissionNote && action === 'deleted') {
    icon = faTrash;
  }

  if (
    type === 'ExportPreprodSubmission' &&
    (action === 'initialized' || action === 'complete' || action === 'failed')
  ) {
    icon = faDownload;
  }



  const activityText = generateActivityCopyText(activity, fieldLabelMap);

  const getDisplayName = () => {
    if (
      activityText.title === 'Submission Export Completed' ||
      activityText.title === 'Submission Export Failed' ||
      activity.isSystem
    ) {
      return 'Morressier System';
    }
    if (userFirstName === 'Invited' && userLastName === 'User') {
      return typeof reviewerEmail === 'string' ? reviewerEmail : '';
    }
    return `${userFirstName} ${userLastName}`;
  };

  return (
    <Box key={`${activity.date}-${activity.type}`}>
      <Flex flexDirection="row" gap={4} alignItems="center">
        <Box alignSelf="start" marginTop={3}>
          <FontAwesomeIcon fontSize="32px" icon={icon} />
        </Box>
        <Flex flexDirection="column" gap={2} width="100%">
          <Flex alignItems="center" gap={4}>
            <Text textStyle="headingLine" textTransform={'capitalize'}>
              {activityText.title}
            </Text>
            {phase && allPhaseMap[phase as ISubmissionWorkflowState] &&  <SubmissionPhase currentState={phase} isHoverDisabled />}
          </Flex>
          <Flex alignItems="center" gap={4} flexWrap="wrap">
            {proxyUserId ? (
              <Flex alignItems="center" gap={2}>
                <Text textStyle="textBody">
                  By {proxyUserFirstName} {proxyUserLastName} on behalf of{' '}
                  {userFirstName} {userLastName}
                </Text>
                {proxyUserStatus === 'deactivated' && <DeactivatedUserIcon />}
                {userStatus === 'deactivated' && <DeactivatedUserIcon />}
              </Flex>
            ) : (
              (userFirstName || userLastName) && (
                <Flex alignItems="center" gap={2}>
                  <Text textStyle="textBody">By {getDisplayName()}</Text>
                  {(userStatus === 'deactivated' && !isSystem)  && <DeactivatedUserIcon />}
                </Flex>
              )
            )}
            {activity?.type === 'ReviewDeadlinePassed' && (
              <Flex alignItems="center" gap={2}>
                <Text textStyle="textBody">
                  {roleContext === 'editorial' && 'For'}{' '}
                  {reviewerFirstName === 'Invited' &&
                  reviewerLastName === 'User'
                    ? reviewerEmail?.toString()
                    : `${reviewerFirstName} ${reviewerLastName}`}
                </Text>
                {reviewerStatus === 'deactivated' && <DeactivatedUserIcon />}
              </Flex>
            )}
            <Text textStyle="textBody">
              {formatDateAndTime(date)}
            </Text>
          </Flex>

          {content ? (
            <Flex flexDirection="column" gap={4}>
              <DisplayMultilineText text={content} textStyle="textContext" />
            </Flex>
          ) : activityText.content ? (
            <Flex alignItems="center" gap={2} flexWrap="wrap">
              {typeof activityText.content === 'string' ? (
                <Text textStyle="textContext">{activityText.content}</Text>
              ) : (
                activityText.content
              )}
              {reviewerStatus === 'deactivated' && <DeactivatedUserIcon />}
            </Flex>
          ) : null}
          {typeof activity.emailCount === 'number' &&
            activity.emailCount > 0 && (
              <ButtonText
                alignSelf="flex-start"
                onClick={() => {
                  activity.eventId && setIsEmailModalOpen(true);
                }}
              >
                View Triggered {(activity.emailCount || 0) > 1
                  ? `Mails (${activity.emailCount})`
                  : 'Mail'}
              </ButtonText>
            )}
        </Flex>
      </Flex>
      {activity.eventId && isEmailModalOpen && (
        <EmailViewModal
          submissionContextId={submissionContextId}
          submissionId={submissionId}
          onClose={() => setIsEmailModalOpen(false)}
          event={{ ...activity, eventId: activity.eventId! }}
          roleContext={roleContext}
          reviewId={reviewId}
        />
      )}
    </Box>
  );
};

export default ActivityBase;
